// src/components/dashboard/SavedFoldersView.tsx
// ✅ FIX: "Todos" usa toggle unsave (bookmark) em vez de deletar da pasta
// ✅ FIX: Estudar navega para /study/saved corretamente
// ✅ FIX: Contagem atualiza ao dessalvar
// ✅ FIX: Ícones Lucide (não emoji) + dark mode branco
// ✅ FIX: Botões do dialog centralizados

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DndContext, closestCenter, PointerSensor,
    KeyboardSensor, useSensor, useSensors, DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext, sortableKeyboardCoordinates,
    useSortable, rectSortingStrategy, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Folder, FolderHeart, FolderSearch, FolderClock, FolderKanban,
    Rocket, Zap, Sparkles, Coffee, Ghost, Star, Flame, Brain,
    Target, Trophy, Lightbulb, Music, Leaf, Shield, Crown,
    Compass, Archive, Tag, Pizza, Banana,
    Plus, GripVertical, Trash2, Edit2, ArrowLeft, BookOpen, Bookmark,
} from 'lucide-react';
import OrganizeModal from '../study/OrganizeModal';
import './SavedFoldersView.css';

const API_BASE = '/api';
const token = () => localStorage.getItem('accessToken');
const authHeader = () => ({ Authorization: `Bearer ${token()}` });

// ========== MAPA DE ÍCONES ==========
const ICON_MAP: Record<string, React.ElementType> = {
    Folder, FolderHeart, FolderSearch, FolderClock, FolderKanban,
    Rocket, Zap, Sparkles, Coffee, Ghost, Star, Flame, Brain,
    Target, Trophy, Lightbulb, Music, Leaf, Shield, Crown,
    Compass, Archive, Tag, Pizza, Banana,
};

const ICON_OPTIONS: { id: string; label: string }[] = [
    { id: 'Folder', label: 'Pasta' },
    { id: 'FolderHeart', label: 'Favoritos' },
    { id: 'FolderSearch', label: 'Pesquisa' },
    { id: 'FolderClock', label: 'Recentes' },
    { id: 'FolderCode2', label: 'Código' },
    { id: 'FolderKanban', label: 'Projeto' },
    { id: 'Rocket', label: 'Lançamento' },
    { id: 'Zap', label: 'Rápido' },
    { id: 'Sparkles', label: 'Destaque' },
    { id: 'Coffee', label: 'Casual' },
    { id: 'Ghost', label: 'Especial' },
    { id: 'Star', label: 'Estrela' },
    { id: 'Flame', label: 'Em fogo' },
    { id: 'Brain', label: 'Conhecimento' },
    { id: 'Target', label: 'Foco' },
    { id: 'Trophy', label: 'Conquista' },
    { id: 'Lightbulb', label: 'Ideias' },
    { id: 'Music', label: 'Música' },
    { id: 'Leaf', label: 'Natureza' },
    { id: 'Shield', label: 'Importante' },
    { id: 'Crown', label: 'Top' },
    { id: 'Compass', label: 'Explorar' },
    { id: 'Pizza', label: 'Descanso' },
    { id: 'Banana', label: 'Inusitado' },
];

const getIcon = (id: string): React.ElementType => ICON_MAP[id] ?? Folder;

// ========== INTERFACES ==========
interface SavedFolder {
    _id: string;
    name: string;
    icon: string;
    color: string;
    cardCount: number;
    order: number;
}

interface SavedCard {
    _id: string;
    cardId: string;        // id original do card (para unsave)
    deckId: string;
    subdeckId: string;
    hierarchy: { cycleName: string | null; deckName: string; subdeckName: string; };
    cardContent: { front: string; back: string; };
    savedAt: string;
}

interface SavedFoldersViewProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    onStudySaved: (folderId?: string) => void;
    onRegisterNewFolder?: (fn: () => void) => void;
}

// ========== CARD ITEM (pasta "Todos") — toggle unsave ==========
const AllCardsItem: React.FC<{
    card: SavedCard;
    onUnsave: (card: SavedCard) => void;
    onOrganize: (savedCardId: string) => void;
}> = ({ card, onUnsave, onOrganize }) => {
    const [showBack, setShowBack] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    const stripHtml = (html: string) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const handleUnsave = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsRemoving(true);
        await onUnsave(card);
        // componente vai sumir, não precisa resetar
    };

    return (
        <div className={`folder-card-item ${isRemoving ? 'removing' : ''}`} onClick={() => setShowBack(v => !v)}>
            <div className="folder-card-header">
                <div className="folder-card-hierarchy">
                    <span>{card.hierarchy.deckName}</span>
                    <span className="sep">→</span>
                    <span>{card.hierarchy.subdeckName}</span>
                </div>
                <div className="folder-card-actions" onClick={e => e.stopPropagation()}>
                    <button className="folder-card-btn" onClick={() => onOrganize(card._id)} title="Organizar em pasta">
                        <Folder size={13} />
                    </button>
                    {/* ✅ Toggle unsave — bookmark ativo, ao clicar remove dos salvos */}
                    <button
                        className="folder-card-btn saved-toggle"
                        onClick={handleUnsave}
                        title="Remover dos salvos"
                        disabled={isRemoving}
                    >
                        <Bookmark size={13} fill="currentColor" />
                    </button>
                </div>
            </div>
            <div className={`folder-card-body ${showBack ? 'flipped' : ''}`}>
                <div className="folder-card-label">{showBack ? 'Resposta' : 'Pergunta'}</div>
                <div className="folder-card-text">
                    {stripHtml(showBack ? card.cardContent.back : card.cardContent.front)}
                </div>
            </div>
            <div className="folder-card-hint">Toque para virar</div>
        </div>
    );
};

// ========== CARD ITEM (pastas criadas) — deletar da pasta ==========
const FolderCardItem: React.FC<{
    card: SavedCard;
    folderId: string;
    onRemoveFromFolder: (savedCardId: string, folderId: string) => void;
    onOrganize: (savedCardId: string) => void;
}> = ({ card, folderId, onRemoveFromFolder, onOrganize }) => {
    const [showBack, setShowBack] = useState(false);

    const stripHtml = (html: string) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    return (
        <div className="folder-card-item" onClick={() => setShowBack(v => !v)}>
            <div className="folder-card-header">
                <div className="folder-card-hierarchy">
                    <span>{card.hierarchy.deckName}</span>
                    <span className="sep">→</span>
                    <span>{card.hierarchy.subdeckName}</span>
                </div>
                <div className="folder-card-actions" onClick={e => e.stopPropagation()}>
                    <button className="folder-card-btn" onClick={() => onOrganize(card._id)} title="Mover para outra pasta">
                        <Folder size={13} />
                    </button>
                    <button className="folder-card-btn danger" onClick={() => onRemoveFromFolder(card._id, folderId)} title="Remover desta pasta">
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>
            <div className={`folder-card-body ${showBack ? 'flipped' : ''}`}>
                <div className="folder-card-label">{showBack ? 'Resposta' : 'Pergunta'}</div>
                <div className="folder-card-text">
                    {stripHtml(showBack ? card.cardContent.back : card.cardContent.front)}
                </div>
            </div>
            <div className="folder-card-hint">Toque para virar</div>
        </div>
    );
};

// ========== SORTABLE FOLDER CARD ==========
const SortableFolderCard: React.FC<{
    folder: SavedFolder;
    onOpen: () => void;
    onEdit: () => void;
    onDelete: () => void;
}> = ({ folder, onOpen, onEdit, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: folder._id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
    const IconComp = getIcon(folder.icon);

    return (
        <div ref={setNodeRef} style={style} className={`sfv-folder-card ${isDragging ? 'dragging' : ''}`} onClick={onOpen}>
            <div className="sfv-folder-card-drag" {...attributes} {...listeners} onClick={e => e.stopPropagation()}>
                <GripVertical size={14} />
            </div>
            <div className="sfv-folder-card-icon" style={{ background: `${folder.color}18`, border: `1.5px solid ${folder.color}30` }}>
                {/* ✅ Dark mode: ícone usa cor da pasta (visível em ambos os temas) */}
                <IconComp size={22} strokeWidth={1.8} color={folder.color} />
            </div>
            <div className="sfv-folder-card-info">
                <span className="sfv-folder-card-name">{folder.name}</span>
                <span className="sfv-folder-card-count">
                    {folder.cardCount} card{folder.cardCount !== 1 ? 's' : ''}
                </span>
            </div>
            <div className="sfv-folder-card-btns" onClick={e => e.stopPropagation()}>
                <button className="sfv-folder-btn edit" onClick={onEdit} title="Renomear"><Edit2 size={13} /></button>
                <button className="sfv-folder-btn delete" onClick={onDelete} title="Deletar pasta"><Trash2 size={13} /></button>
            </div>
        </div>
    );
};

// ========== COMPONENTE PRINCIPAL ==========
const SavedFoldersView: React.FC<SavedFoldersViewProps> = ({ onShowToast, onStudySaved, onRegisterNewFolder }) => {
    const navigate = useNavigate();

    const [folders, setFolders] = useState<SavedFolder[]>([]);
    const [totalCards, setTotalCards] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const [openFolder, setOpenFolder] = useState<SavedFolder | null>(null);
    const [folderCards, setFolderCards] = useState<SavedCard[]>([]);
    const [isLoadingCards, setIsLoadingCards] = useState(false);

    const [allCards, setAllCards] = useState<SavedCard[]>([]);
    const [isViewingAll, setIsViewingAll] = useState(false);

    const [organizeCardId, setOrganizeCardId] = useState<string | null>(null);
    const [showOrganizeModal, setShowOrganizeModal] = useState(false);

    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<SavedFolder | null>(null);
    const [folderName, setFolderName] = useState('');
    const [folderIcon, setFolderIcon] = useState('Folder');

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // ========== FETCH ==========
    const fetchFolders = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_BASE}/saved-folders`, { headers: authHeader() });
            if (res.ok) {
                const data = await res.json();
                setFolders(data.folders || []);
                setTotalCards(data.totalCards || 0);
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchAllCards = useCallback(async () => {
        setIsLoadingCards(true);
        try {
            const res = await fetch(`${API_BASE}/saved-cards`, { headers: authHeader() });
            if (res.ok) {
                const data = await res.json();
                setAllCards(data.savedCards || []);
            }
        } catch { onShowToast('Erro ao carregar cards', 'error'); }
        finally { setIsLoadingCards(false); }
    }, [onShowToast]);

    const fetchFolderCards = useCallback(async (folderId: string) => {
        setIsLoadingCards(true);
        try {
            const res = await fetch(`${API_BASE}/saved-folders/${folderId}/cards`, { headers: authHeader() });
            if (res.ok) {
                const data = await res.json();
                setFolderCards(data.cards || []);
            }
        } catch { onShowToast('Erro ao carregar cards', 'error'); }
        finally { setIsLoadingCards(false); }
    }, [onShowToast]);

    useEffect(() => { fetchFolders(); }, [fetchFolders]);

    useEffect(() => {
        onRegisterNewFolder?.(() => { setFolderName(''); setFolderIcon('Folder'); setShowCreateDialog(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ========== NAVEGAÇÃO ==========
    const handleOpenFolder = (folder: SavedFolder) => {
        setOpenFolder(folder);
        setIsViewingAll(false);
        fetchFolderCards(folder._id);
    };

    const handleOpenAll = () => {
        setIsViewingAll(true);
        setOpenFolder(null);
        fetchAllCards();
    };

    const handleBack = () => {
        setOpenFolder(null);
        setIsViewingAll(false);
        setFolderCards([]);
        setAllCards([]);
        fetchFolders();
    };

    // ========== ESTUDAR ==========
    const handleStudy = (folderId?: string) => {
        sessionStorage.setItem('graia_theme_context', 'enem');
        if (folderId) {
            navigate(`/study/saved?folder=${folderId}`);
        } else {
            navigate('/study/saved');
        }
    };

    // ========== UNSAVE (pasta "Todos") ==========
    // ✅ Remove o card dos salvos via toggle — não deleta da pasta, pois não tem pasta
    const handleUnsave = async (card: SavedCard) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/saved-cards/toggle', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    deckId: card.deckId || 'saved',
                    subdeckId: card.subdeckId || 'saved',
                    cardId: card.cardId,
                    hierarchy: card.hierarchy,
                    cardContent: card.cardContent
                })
            });
            if (response.ok) {
                setAllCards(prev => prev.filter(c => c._id !== card._id));
                setTotalCards(prev => Math.max(0, prev - 1));
                onShowToast('Flashcard removido dos salvos', 'info');
                fetchFolders(); // atualiza contagem total
            }
        } catch {
            onShowToast('Erro ao remover card', 'error');
        }
    };

    // ========== REMOVER DA PASTA CRIADA ==========
    const handleRemoveFromFolder = async (savedCardId: string, folderId: string) => {
        try {
            const res = await fetch(`${API_BASE}/saved-folders/${folderId}/cards/${savedCardId}`, {
                method: 'DELETE', headers: authHeader()
            });
            if (res.ok) {
                setFolderCards(prev => prev.filter(c => c._id !== savedCardId));
                setFolders(prev => prev.map(f =>
                    f._id === folderId ? { ...f, cardCount: Math.max(0, f.cardCount - 1) } : f
                ));
                onShowToast('Removido da pasta', 'info');
                fetchFolders();
            }
        } catch { onShowToast('Erro ao remover', 'error'); }
    };

    // ========== CRUD PASTAS ==========
    const handleCreateFolder = async () => {
        if (!folderName.trim()) { onShowToast('Digite um nome', 'error'); return; }
        try {
            const res = await fetch(`${API_BASE}/saved-folders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ name: folderName.trim(), icon: folderIcon })
            });
            if (res.ok) {
                setShowCreateDialog(false); setFolderName(''); setFolderIcon('Folder');
                fetchFolders(); onShowToast('Pasta criada!', 'success');
            } else {
                const err = await res.json();
                onShowToast(err.message || 'Erro ao criar', 'error');
            }
        } catch { onShowToast('Erro ao criar pasta', 'error'); }
    };

    const handleEditFolder = async () => {
        if (!selectedFolder || !folderName.trim()) { onShowToast('Digite um nome', 'error'); return; }
        try {
            const res = await fetch(`${API_BASE}/saved-folders/${selectedFolder._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ name: folderName.trim(), icon: folderIcon })
            });
            if (res.ok) {
                setShowEditDialog(false); setSelectedFolder(null);
                fetchFolders(); onShowToast('Pasta atualizada!', 'success');
            } else {
                const err = await res.json();
                onShowToast(err.message || 'Erro ao editar', 'error');
            }
        } catch { onShowToast('Erro ao editar pasta', 'error'); }
    };

    const handleDeleteFolder = async () => {
        if (!selectedFolder) return;
        try {
            const res = await fetch(`${API_BASE}/saved-folders/${selectedFolder._id}`, {
                method: 'DELETE', headers: authHeader()
            });
            if (res.ok) {
                setShowDeleteDialog(false); setSelectedFolder(null);
                fetchFolders(); onShowToast('Pasta deletada. Cards continuam salvos.', 'info');
            }
        } catch { onShowToast('Erro ao deletar', 'error'); }
    };

    // ========== DRAG ==========
    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = folders.findIndex(f => f._id === active.id);
        const newIndex = folders.findIndex(f => f._id === over.id);
        const newFolders = arrayMove(folders, oldIndex, newIndex);
        setFolders(newFolders);
        try {
            await fetch(`${API_BASE}/saved-folders/reorder/batch`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ order: newFolders.map((f, i) => ({ folderId: f._id, order: i })) })
            });
        } catch { onShowToast('Erro ao reordenar', 'error'); }
    };

    const handleOrganize = (savedCardId: string) => {
        setOrganizeCardId(savedCardId);
        setShowOrganizeModal(true);
    };

    // ========== ICON PICKER ==========
    const IconPicker = () => (
        <div className="sfv-icon-picker">
            {ICON_OPTIONS.map(({ id, label }) => {
                const Icon = getIcon(id);
                return (
                    <button
                        key={id}
                        className={`sfv-icon-btn ${folderIcon === id ? 'active' : ''}`}
                        onClick={() => setFolderIcon(id)}
                        title={label}
                        type="button"
                    >
                        <Icon size={17} strokeWidth={1.8} />
                    </button>
                );
            })}
        </div>
    );

    // ========== VIEW DE CARDS ==========
    const isInFolderView = openFolder || isViewingAll;

    if (isInFolderView) {
        const cards = isViewingAll ? allCards : folderCards;
        const title = isViewingAll ? 'Todos os Flashcards' : openFolder!.name;
        const folderId = openFolder?._id || '';
        const FolderIconComp = openFolder ? getIcon(openFolder.icon) : Archive;

        return (
            <div className="sfv-container">
                <div className="sfv-breadcrumb">
                    <button className="sfv-back-btn" onClick={handleBack}>
                        <ArrowLeft size={16} />Pastas
                    </button>
                    <span className="sfv-breadcrumb-sep">›</span>
                    <span className="sfv-breadcrumb-current">
                        <FolderIconComp size={15} strokeWidth={1.8} />
                        {title}
                    </span>
                    {cards.length > 0 && (
                        <button className="sfv-study-btn" onClick={() => handleStudy(folderId || undefined)}>
                            <BookOpen size={14} />Estudar pasta
                        </button>
                    )}
                </div>

                {isLoadingCards ? (
                    <div className="sfv-loading"><span className="sfv-spinner" /></div>
                ) : cards.length === 0 ? (
                    <div className="sfv-empty">
                        <Bookmark size={36} strokeWidth={1.5} />
                        <p>Nenhum flashcard nesta pasta</p>
                    </div>
                ) : (
                    <div className="sfv-cards-grid">
                        {isViewingAll
                            ? (allCards as SavedCard[]).map(card => (
                                <AllCardsItem
                                    key={card._id}
                                    card={card}
                                    onUnsave={handleUnsave}
                                    onOrganize={handleOrganize}
                                />
                            ))
                            : (folderCards as SavedCard[]).map(card => (
                                <FolderCardItem
                                    key={card._id}
                                    card={card}
                                    folderId={folderId}
                                    onRemoveFromFolder={handleRemoveFromFolder}
                                    onOrganize={handleOrganize}
                                />
                            ))
                        }
                    </div>
                )}

                <OrganizeModal
                    isOpen={showOrganizeModal}
                    savedCardId={organizeCardId}
                    onClose={() => { setShowOrganizeModal(false); setOrganizeCardId(null); }}
                    onShowToast={onShowToast}
                />
            </div>
        );
    }

    // ========== GRID DE PASTAS ==========
    return (
        <div className="sfv-container">
            {isLoading ? (
                <div className="sfv-loading"><span className="sfv-spinner" /></div>
            ) : (
                <>
                    <div className="sfv-all-card" onClick={handleOpenAll}>
                        <div className="sfv-all-icon">
                            <Archive size={26} strokeWidth={1.6} color="var(--platform-primary)" />
                        </div>
                        <div className="sfv-all-info">
                            <span className="sfv-all-name">Todos os flashcards</span>
                            <span className="sfv-all-count">{totalCards} salvo{totalCards !== 1 ? 's' : ''}</span>
                        </div>
                        <button className="sfv-all-study" onClick={e => { e.stopPropagation(); handleStudy(); }}>
                            <BookOpen size={14} />Estudar todos
                        </button>
                    </div>

                    {folders.length === 0 ? (
                        <div className="sfv-empty-folders">
                            <Folder size={40} strokeWidth={1.2} />
                            <p>Nenhuma pasta criada</p>
                            <span>Organize seus flashcards em pastas para estudar com mais foco</span>
                            <button className="sfv-btn-create-empty" onClick={() => { setFolderName(''); setFolderIcon('Folder'); setShowCreateDialog(true); }}>
                                <Plus size={15} />Criar primeira pasta
                            </button>
                        </div>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={folders.map(f => f._id)} strategy={rectSortingStrategy}>
                                <div className="sfv-folders-grid">
                                    {folders.map(folder => (
                                        <SortableFolderCard
                                            key={folder._id}
                                            folder={folder}
                                            onOpen={() => handleOpenFolder(folder)}
                                            onEdit={() => { setSelectedFolder(folder); setFolderName(folder.name); setFolderIcon(folder.icon); setShowEditDialog(true); }}
                                            onDelete={() => { setSelectedFolder(folder); setShowDeleteDialog(true); }}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </>
            )}

            {/* DIALOGS */}
            {showCreateDialog && (
                <div className="sfv-dialog-overlay" onClick={() => setShowCreateDialog(false)}>
                    <div className="sfv-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Nova pasta</h3>
                        <p className="sfv-dialog-sublabel">Escolha um ícone</p>
                        <IconPicker />
                        <input type="text" placeholder="Nome da pasta" value={folderName} onChange={e => setFolderName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateFolder()} autoFocus maxLength={60} />
                        <div className="sfv-dialog-actions">
                            <button onClick={() => setShowCreateDialog(false)}>Cancelar</button>
                            <button className="btn-primary" onClick={handleCreateFolder}>Criar</button>
                        </div>
                    </div>
                </div>
            )}

            {showEditDialog && selectedFolder && (
                <div className="sfv-dialog-overlay" onClick={() => setShowEditDialog(false)}>
                    <div className="sfv-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Editar pasta</h3>
                        <p className="sfv-dialog-sublabel">Escolha um ícone</p>
                        <IconPicker />
                        <input type="text" placeholder="Nome da pasta" value={folderName} onChange={e => setFolderName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleEditFolder()} autoFocus maxLength={60} />
                        <div className="sfv-dialog-actions">
                            <button onClick={() => setShowEditDialog(false)}>Cancelar</button>
                            <button className="btn-primary" onClick={handleEditFolder}>Salvar</button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteDialog && selectedFolder && (
                <div className="sfv-dialog-overlay" onClick={() => setShowDeleteDialog(false)}>
                    <div className="sfv-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Deletar pasta</h3>
                        <p>Deletar <strong>"{selectedFolder.name}"</strong>?</p>
                        <p className="sfv-dialog-note">Os flashcards continuarão salvos — apenas a pasta será removida.</p>
                        <div className="sfv-dialog-actions">
                            <button onClick={() => setShowDeleteDialog(false)}>Cancelar</button>
                            <button className="btn-danger" onClick={handleDeleteFolder}>Deletar</button>
                        </div>
                    </div>
                </div>
            )}

            <OrganizeModal
                isOpen={showOrganizeModal}
                savedCardId={organizeCardId}
                onClose={() => { setShowOrganizeModal(false); setOrganizeCardId(null); }}
                onShowToast={onShowToast}
            />
        </div>
    );
};

export default SavedFoldersView;